import { useState } from "react";
import { setMe, useCurrentUser } from "_graphql/cache/auth";
import toast from "react-hot-toast";
import useApi from "utils/use-api";
import SignatureModal, { SignatureModalMode } from "./signature";

type Props = {
    open: boolean;
    setOpen: (val: boolean) => void;
    mode: SignatureModalMode;
};

const dataUrlToFile = (dataUrl: string): File => {
    const [header, base64] = dataUrl.split(",");
    const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
    const extension = mime.split("/")[1] ?? "png";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], `signature.${extension}`, { type: mime });
};

const SignatureContainer = ({ open, setOpen, mode }: Props) => {
    const api = useApi();
    const currentUser = useCurrentUser();
    const [loading, setLoading] = useState(false);

    const handleSave = async (dataUrl: string) => {
        const employeeId = currentUser?.employee?.employee_id;
        if (!employeeId) {
            toast.error("Missing employee information.");
            return;
        }

        const formData = new FormData();
        formData.append("signature", dataUrlToFile(dataUrl));

        setLoading(true);
        try {
            const { data } = await api.post(
                `/api/employees/${employeeId}/signature`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                },
            );

            const signatureUrl =
                data?.signature ?? data?.data?.signature ?? dataUrl;

            setMe(
                Object.assign({}, currentUser, {
                    employee: Object.assign({}, currentUser?.employee, {
                        signature: signatureUrl,
                    }),
                }),
            );
            toast.success("Signature saved successfully");
            setOpen(false);
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ??
                    error?.message ??
                    "Failed to save signature",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <SignatureModal
            open={open}
            setOpen={setOpen}
            mode={mode}
            signature={currentUser?.employee?.signature}
            onSave={mode === "view" ? undefined : handleSave}
            loading={loading}
        />
    );
};

export default SignatureContainer;
